import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { PlatformCategoryApi } from "@/api/platformCategory.api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { APIClient, HandleError } from "@/api/axiosClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface FlightBookingFormFields {
  productCode?: string;
  productName?: string;
  currencyId?: string;
  loyyalCommissionPercentage?: string;
  fulfilmentType?: string;
  redemptionMerchantId?: string;
}

const CURRENCY_OPTIONS = [
  {
    iso3CurrencyCode: "USD",
    currencyName: "United States Dollar",
    currencySymbol: "$",
    id: 1,
  },
  { iso3CurrencyCode: "EUR", currencyName: "Euro", currencySymbol: "€", id: 2 },
  {
    iso3CurrencyCode: "GBP",
    currencyName: "British Pound",
    currencySymbol: "£",
    id: 3,
  },
  {
    iso3CurrencyCode: "INR",
    currencyName: "Indian Rupee",
    currencySymbol: "₹",
    id: 4,
  },
  {
    iso3CurrencyCode: "AED",
    currencyName: "United Arab Emirates Dirham",
    currencySymbol: "د.إ",
    id: 5,
  },
  {
    iso3CurrencyCode: "ETB",
    currencyName: "Ethiopian Birr",
    currencySymbol: "Br",
    id: 6,
  },
];

const FlightBookingProductCreatePage = () => {
  const navigate = useNavigate();
  const [productImage, setProductImage] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [merchants, setMerchants] = React.useState([]);
  const [merchantsLoading, setMerchantsLoading] = React.useState(true);
  const [categories, setCategories] = React.useState([]);
  const [categoriesLoading, setCategoriesLoading] = React.useState(false);
  const [selectedMerchant, setSelectedMerchant] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const [selectedFulfilmentType, setSelectedFulfilmentType] =
    React.useState("");
  const [categoryDialogOpen, setCategoryDialogOpen] = React.useState(false);
  const [formD, setFormD] = React.useState<FlightBookingFormFields>({});
  const [htmlText, setHtmlText] = React.useState("");
  const [files, setFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = React.useState<
    number[]
  >([]);
  const [selectedCurrency, setSelectedCurrency] = React.useState(null);

  React.useEffect(() => {
    async function fetchMerchants() {
      setMerchantsLoading(true);
      try {
        const { data } =
          await RedemptionMerchantApi.fetchAllRedemptionMerchants();
        setMerchants(data?.redemptionMerchantVOS || []);
      } catch (err) {
        setMerchants([]);
        toast.error("Failed to fetch merchants");
      } finally {
        setMerchantsLoading(false);
      }
    }
    fetchMerchants();
  }, []);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const { data } = await PlatformCategoryApi.fetchCategoryTree();
      setCategories(data?.subCategoryVOs || []);
    } catch (err) {
      setCategories([]);
      toast.error("Failed to fetch categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormD((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []) as File[];
    setFiles(selectedFiles);
    if (selectedFiles.length > 0 && selectedFiles[0] instanceof File) {
      setProductImage(selectedFiles[0]);
      setImagePreview(URL.createObjectURL(selectedFiles[0]));
    }
  };

  const renderCategories = (categories) => (
    <div className="flex flex-col gap-2">
      {categories.map((cat) => {
        const isExpanded = expandedCategoryIds.includes(cat.id);
        const hasSub = cat.subCategoryVOs && cat.subCategoryVOs.length > 0;
        return (
          <div key={cat.id} className="ml-2">
            <div
              className="flex items-center gap-2 border rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-50"
              onClick={() => {
                if (hasSub) {
                  setExpandedCategoryIds((ids) =>
                    isExpanded
                      ? ids.filter((id) => id !== cat.id)
                      : [...ids, cat.id]
                  );
                } else {
                  setSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }
              }}
            >
              <span className="flex-1">{cat.categoryName}</span>
              {hasSub && <span>{isExpanded ? "▼" : "▶"}</span>}
              <input
                type="radio"
                checked={selectedCategory?.id === cat.id}
                onChange={() => {
                  setSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }}
                onClick={(e) => e.stopPropagation()}
                readOnly
              />
            </div>
            {hasSub && isExpanded && renderCategories(cat.subCategoryVOs)}
          </div>
        );
      })}
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let updatedFormData = {};
    try {
      updatedFormData = {
        productCode: formD?.productCode,
        productName: formD?.productName,
        redemptionMerchantVO: {
          id: formD?.redemptionMerchantId || selectedMerchant,
        },
        productType: "FLIGHT_BOOKING",
        termsAndConditions: htmlText,
        currencyVO: selectedCurrency ? { id: selectedCurrency.id } : undefined,
        loyyalCommissionPercentage: formD?.loyyalCommissionPercentage,
        fulfilmentType: formD?.fulfilmentType || selectedFulfilmentType,
        platformCategoryIds: selectedCategory ? [selectedCategory.id] : [],
      };
      const formData = new FormData();
      if (productImage) {
        formData.append("giftCardProductLogo", productImage);
      }
      const blob = new Blob([JSON.stringify(updatedFormData)], {
        type: "application/json",
      });
      formData.append("giftCardProductVO", blob);
      await APIClient("/product/createOrUpdateGiftCardProduct", formData, {
        requestType: "post",
        contentType: "multipart/form-data",
      });
      toast.success("Flight details added successfully");
      // Navigate back to flight booking products screen after successful creation
      navigate("/rewardpoint/products/flight-booking");
      setFormD({});
      setHtmlText("");
      setProductImage(null);
      setImagePreview(null);
      setSelectedMerchant(null);
      setSelectedCategory(null);
      setSelectedFulfilmentType("");
      setFiles([]);
    } catch (error) {
      toast.error(HandleError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 w-full py-8 px-8">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to="/rewardpoint/products/flight-booking">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Create Flight Booking Product
          </h1>
          <p className="text-gray-500">
            Add a new flight booking product to your catalog
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-8">
            {/* General Information */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  {/* Product Image */}
                  <div>
                    <label className="block font-medium mb-2">
                      Product Image
                    </label>
                    <div className="w-28 h-28 rounded-lg bg-gray-100 flex items-center justify-center mb-2 relative">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id="flight-image-upload"
                        onChange={handleFileChange}
                      />
                      <label htmlFor="flight-image-upload">
                        <Button variant="ghost" size="icon" asChild>
                          <span>
                            <Plus className="h-8 w-8 text-gray-400" />
                          </span>
                        </Button>
                      </label>
                      {imagePreview && (
                        <img
                          src={imagePreview}
                          alt="Product Preview"
                          className="absolute inset-0 w-24 h-24 object-contain rounded"
                        />
                      )}
                    </div>
                  </div>

                  {/* Flight Inventory Vendor */}
                  <div>
                    <label className="block font-medium mb-1">
                      Flight Inventory Vendor*
                    </label>
                    <Select
                      value={selectedMerchant}
                      onValueChange={setSelectedMerchant}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {merchantsLoading ? (
                          <div className="px-3 py-2 text-gray-500">
                            Loading...
                          </div>
                        ) : merchants.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500">
                            No merchants
                          </div>
                        ) : (
                          merchants.map((m) => (
                            <SelectItem key={m.id} value={m.id + ""}>
                              {m.merchantName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Product Name & Code */}
                  <div className="flex-1">
                    <label className="block font-medium mb-1">
                      Product Name*
                    </label>
                    <Input
                      name="productName"
                      value={formD.productName || ""}
                      onChange={handleInputChange}
                      placeholder="Enter product name"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block font-medium mb-1">
                      Product Code*
                    </label>
                    <Input
                      name="productCode"
                      value={formD.productCode || ""}
                      onChange={handleInputChange}
                      placeholder="Enter product code.."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Flight Booking Details */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Flight Booking Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Merchant-side order placement Mechanism */}
                  <div>
                    <label className="block font-medium mb-1">
                      Merchant-side order placement Mechanism
                    </label>
                    <Select
                      value={selectedFulfilmentType}
                      onValueChange={setSelectedFulfilmentType}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Fulfilment Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MERCHANT_API">
                          MERCHANT_API
                        </SelectItem>
                        <SelectItem value="MERCHANT_EMAIL">
                          MERCHANT_EMAIL
                        </SelectItem>
                        <SelectItem value="LOYYAL_GENERATED">
                          LOYYAL_GENERATED
                        </SelectItem>
                        <SelectItem value="MERCHANT_UPLOADED">
                          MERCHANT_UPLOADED
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Platform Category */}
                  <div>
                    <label className="block font-medium mb-1">
                      Platform Category
                    </label>
                    <Dialog
                      open={categoryDialogOpen}
                      onOpenChange={setCategoryDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full text-left"
                          onClick={fetchCategories}
                        >
                          {selectedCategory
                            ? selectedCategory.categoryName
                            : "Select"}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md w-full p-6">
                        <div className="rounded-xl border p-6 bg-white">
                          {categoriesLoading ? (
                            <div className="text-center text-gray-500 py-8">
                              Loading...
                            </div>
                          ) : (
                            renderCategories(categories)
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-8">
            {/* Pricing & Terms */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Pricing & Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div className="flex-1">
                    <label className="block font-medium mb-1">Currency*</label>
                    <Select
                      value={selectedCurrency?.id || ""}
                      onValueChange={(v) =>
                        setSelectedCurrency(
                          CURRENCY_OPTIONS.find((c) => c.id === Number(v))
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id.toString()}>
                            {opt.currencyName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="block font-medium mb-1">
                      Loyyal Commission* (%)
                    </label>
                    <Input
                      name="loyyalCommissionPercentage"
                      value={formD.loyyalCommissionPercentage || ""}
                      onChange={handleInputChange}
                      placeholder="Commission"
                    />
                  </div>
                </div>
                {/* Terms & Conditions */}
                <div>
                  <label className="block font-medium mb-1">
                    Terms & Conditions
                  </label>
                  <Textarea
                    value={htmlText}
                    onChange={(e) => setHtmlText(e.target.value)}
                    placeholder="Enter terms & conditions"
                    rows={5}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Separator className="my-8" />
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => window.history.back()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Create Product"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FlightBookingProductCreatePage;
